import {
  type InvalidateQueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { produce } from 'immer';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  REACT_QUERY_KEYS,
  StatusCode,
  type PaginatedCustomerTagListList,
  patchApiV2CustomersCustomerIdTags,
  type CustomerTagList,
  type PatchedCustomerTagList,
} from '@hungryroot/rest';

interface Options {
  invalidationKeys?: InvalidateQueryFilters[];
}

export function usePatchCustomerTags(options: Options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { data: PatchedCustomerTagList[] }) => {
      const { data } = params;
      const res = await patchApiV2CustomersCustomerIdTags({
        requestBody: data,
      });

      if (res.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse('Failed to patch customer tags', {
          key: 'tags.usePatchCustomerTags.unexpected_status',
          status: res.status,
          numTags: data.length,
        });
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async (newTag) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
      });
      const existingCustomerTags =
        queryClient.getQueriesData<PaginatedCustomerTagListList>({
          queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
        });

      existingCustomerTags.forEach(([queryKey, results]) => {
        const next = results
          ? produce(results, (draft) => {
              const tagMap = draft.results.reduce(
                (seed, tag) => {
                  seed[tag.tag] = tag;
                  return seed;
                },
                {} as Record<
                  string,
                  PaginatedCustomerTagListList['results'][number]
                >
              );

              newTag.data.forEach((result) => {
                const match = tagMap[result.tag];

                if (match === undefined) {
                  draft.results.push(result as CustomerTagList);
                  return;
                }

                match.preference = result.preference;
              });
            })
          : {
              results: newTag.data,
            };
        queryClient.setQueryData(queryKey, next);
      });
      return { existingCustomerTags };
    },
    onError: (
      _err,
      _newTag,
      context:
        | {
            existingCustomerTags: unknown;
          }
        | undefined
    ) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
        context?.existingCustomerTags
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
      });

      if (options.invalidationKeys) {
        options.invalidationKeys.forEach((invalidate) => {
          queryClient.invalidateQueries(invalidate);
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['FoodProfile'],
      });
    },
  });
}
